import type {
  DividedPart,
  JumpSummary,
  LyricsSummary,
  MatchedBy,
  ParsedPart,
  ParsedScore,
  PartAssignment,
  PartRole,
  PartSummary,
  RepeatSummary,
  ScoreSummary,
  SummaryWarning,
  TempoMark,
  TempoSummary,
  VoicePart,
} from './model';
import { VOICE_PARTS } from './model';
import { buildTimeline } from './timeline';
import { unrollMeasures } from './unroll';
import { matchVoicePart } from './voices';

/** Version of the ScoreSummary shape stored with each song. */
export const SUMMARY_VERSION = 1;

/** A tenor in a treble-8 clef whose median note is above this MIDI pitch (F4) is probably an octave off. */
const OCTAVE_CLEF_SUSPICIOUS_MEDIAN = 65;

const PREVIEW_WORDS = 10;

export interface SummarizeOptions {
  /** Assignments chosen by the director, by part id. They replace the automatic matching. */
  assignments?: Record<string, PartAssignment>;
}

interface Assignment {
  role: PartRole;
  voicePart: VoicePart | null;
  matchedBy: MatchedBy;
}

function hasLyrics(part: ParsedPart): boolean {
  return part.notes.some((n) => n.lyrics.length > 0);
}

/** Decide which parts are voices (and which voice) and which are accompaniment. */
export function assignParts(score: ParsedScore, overrides: Record<string, PartAssignment> = {}): Map<string, Assignment> {
  const result = new Map<string, Assignment>();
  for (const part of score.parts) {
    const override = overrides[part.id];
    if (override) {
      result.set(
        part.id,
        override === 'accompaniment'
          ? { role: 'accompaniment', voicePart: null, matchedBy: 'manual' }
          : { role: 'voice', voicePart: override, matchedBy: 'manual' },
      );
      continue;
    }
    const match = part.staffCount === 1 ? matchVoicePart(part.name, part.abbreviation, ...part.instrumentNames) : null;
    if (match) result.set(part.id, { role: 'voice', voicePart: match, matchedBy: 'name' });
    else if (hasLyrics(part)) result.set(part.id, { role: 'voice', voicePart: null, matchedBy: null });
    else result.set(part.id, { role: 'accompaniment', voicePart: null, matchedBy: null });
  }

  // Four unnamed single-staff voice parts and nothing matched: assume S, A, T, B from the top.
  const voices = score.parts.filter((p) => result.get(p.id)?.role === 'voice');
  const anyMatched = voices.some((p) => result.get(p.id)?.voicePart !== null);
  if (!anyMatched && voices.length === 4 && voices.every((p) => p.staffCount === 1 && !overrides[p.id])) {
    voices.forEach((p, i) => {
      result.set(p.id, { role: 'voice', voicePart: VOICE_PARTS[i] as VoicePart, matchedBy: 'order' });
    });
  }
  return result;
}

function summarizeLyrics(part: ParsedPart): LyricsSummary {
  const verses = new Set<number>();
  let syllableCount = 0;
  let notesWithLyrics = 0;
  for (const note of part.notes) {
    if (note.lyrics.length > 0) notesWithLyrics++;
    for (const l of note.lyrics) {
      verses.add(l.verse);
      syllableCount++;
    }
  }
  const sortedVerses = [...verses].sort((a, b) => a - b);
  const firstVerse = sortedVerses[0];
  const words: string[] = [];
  let current = '';
  for (const note of part.notes) {
    if (words.length >= PREVIEW_WORDS) break;
    const l = note.lyrics.find((x) => x.verse === firstVerse);
    if (!l) continue;
    current += l.text;
    if (l.syllabic === 'single' || l.syllabic === 'end') {
      words.push(current);
      current = '';
    }
  }
  if (current && words.length < PREVIEW_WORDS) words.push(current);
  const truncated = words.length >= PREVIEW_WORDS;
  return {
    syllableCount,
    verses: sortedVerses,
    coverage: part.notes.length > 0 ? Math.round((notesWithLyrics / part.notes.length) * 1000) / 1000 : 0,
    preview: words.join(' ') + (truncated ? ' …' : ''),
  };
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] as number) : ((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2;
}

function summarizeRepeats(score: ParsedScore): RepeatSummary[] {
  const repeats: RepeatSummary[] = [];
  const num = (i: number): string => score.measures[i]?.number ?? String(i + 1);
  let sectionStart = 0;
  let current: RepeatSummary | null = null;

  score.measures.forEach((m, i) => {
    if (m.forwardRepeat && !m.ending) sectionStart = i;
    if (m.ending) {
      if (!current) {
        current = {
          startMeasureNumber: num(sectionStart),
          endMeasureNumber: num(Math.max(sectionStart, i - 1)),
          times: m.ending.groupMax,
          endings: [],
        };
        repeats.push(current);
      }
      const last = current.endings[current.endings.length - 1];
      if (m.endingStart || !last) {
        current.endings.push({ numbers: m.ending.numbers, firstMeasureNumber: m.number, lastMeasureNumber: m.number });
      } else {
        last.lastMeasureNumber = m.number;
      }
      if (!score.measures[i + 1]?.ending) {
        current = null;
        sectionStart = i + 1;
      }
      return;
    }
    if (m.backwardRepeat) {
      repeats.push({
        startMeasureNumber: num(sectionStart),
        endMeasureNumber: m.number,
        times: m.backwardRepeat.times,
        endings: [],
      });
      sectionStart = i + 1;
    }
  });
  return repeats;
}

function summarizeJumps(score: ParsedScore): JumpSummary[] {
  const jumps: JumpSummary[] = [];
  const types = ['segno', 'coda', 'toCoda', 'daCapo', 'dalSegno', 'fine'] as const;
  for (const m of score.measures) {
    for (const type of types) {
      if (m[type]) jumps.push({ type, measureNumber: m.number });
    }
  }
  return jumps;
}

function tempoSummary(score: ParsedScore, mark: TempoMark): TempoSummary {
  return {
    measureNumber: score.measures[mark.measureIndex]?.number ?? String(mark.measureIndex + 1),
    quarterBpm: Math.round(mark.quarterBpm * 1000) / 1000,
    beatUnit: mark.beatUnit,
    dotted: mark.dotted,
    perMinute: mark.perMinute,
  };
}

/**
 * Voice parts that divide into two lines, and the bars where the lines differ: two voices or
 * chords on the part's staff, or two staves assigned the same part (Soprano 1 and 2).
 */
function summarizeDivisions(score: ParsedScore, voices: readonly PartSummary[]): DividedPart[] {
  const out: DividedPart[] = [];
  const num = (i: number): string => score.measures[i]?.number ?? String(i + 1);
  const byIndex = (indexes: Set<number>) => [...indexes].sort((a, b) => a - b).map(num);
  for (const vp of VOICE_PARTS) {
    const parts = voices.filter((p) => p.voicePart === vp).map((p) => score.parts.find((x) => x.id === p.id) as ParsedPart);
    if (parts.length === 2) {
      // Bars where the two staves do not sing the same notes.
      const sig = (part: ParsedPart, m: number) =>
        part.notes
          .filter((n) => n.measureIndex === m)
          .map((n) => `${n.offset}:${n.duration}:${n.midi}`)
          .sort()
          .join(' ');
      const differ = new Set<number>();
      score.measures.forEach((_, m) => {
        if (sig(parts[0] as ParsedPart, m) !== sig(parts[1] as ParsedPart, m)) differ.add(m);
      });
      out.push({ voicePart: vp, source: 'staves', measureNumbers: byIndex(differ) });
    } else if (parts.length === 1) {
      const divided = new Set((parts[0] as ParsedPart).notes.filter((n) => n.line !== 0).map((n) => n.measureIndex));
      if (divided.size > 0) out.push({ voicePart: vp, source: 'staff', measureNumbers: byIndex(divided) });
    }
  }
  return out;
}

/** Summarise a parsed score for the upload screen and for storage with the song. */
export function summarizeScore(score: ParsedScore, options: SummarizeOptions = {}): ScoreSummary {
  const assignments = assignParts(score, options.assignments);
  const warnings: SummaryWarning[] = [...score.readWarnings];

  const parts: PartSummary[] = score.parts.map((part) => {
    const a = assignments.get(part.id) as Assignment;
    const midis = part.notes.map((n) => n.midi);
    return {
      id: part.id,
      name: part.name,
      abbreviation: part.abbreviation,
      role: a.role,
      voicePart: a.voicePart,
      matchedBy: a.matchedBy,
      staffCount: part.staffCount,
      clefs: part.clefs,
      treble8vb: part.clefs.some((c) => c.kind === 'treble8vb'),
      noteCount: part.notes.length,
      range: midis.length > 0 ? { lowestMidi: Math.min(...midis), highestMidi: Math.max(...midis) } : null,
      lyrics: summarizeLyrics(part),
    };
  });

  const voices = parts.filter((p) => p.role === 'voice');
  if (voices.length === 0) {
    warnings.push({ code: 'noVoiceParts' });
  } else {
    for (const vp of VOICE_PARTS) {
      const count = voices.filter((p) => p.voicePart === vp).length;
      if (count === 0) warnings.push({ code: 'missingVoicePart', voicePart: vp });
      // Two staves for one part are its two lines (Soprano 1 and 2); more cannot be told apart.
      if (count > 2) warnings.push({ code: 'duplicateVoicePart', voicePart: vp });
    }
  }
  for (const p of voices) {
    const parsed = score.parts.find((x) => x.id === p.id) as ParsedPart;
    if (p.voicePart === null) warnings.push({ code: 'unmatchedVoice', partId: p.id });
    if (p.matchedBy === 'order') warnings.push({ code: 'matchedByOrder', partId: p.id });
    if (p.lyrics.syllableCount === 0) warnings.push({ code: 'noLyrics', partId: p.id });
    if (p.staffCount > 1) warnings.push({ code: 'multiStaffVoice', partId: p.id });
    if (parsed.notes.some((n) => n.line === 3)) warnings.push({ code: 'moreThanTwoLines', partId: p.id });
    if (parsed.graceNoteCount > 0) warnings.push({ code: 'graceNotesIgnored', partId: p.id });
    if (p.treble8vb && parsed.notes.length > 0 && median(parsed.notes.map((n) => n.midi)) > OCTAVE_CLEF_SUSPICIOUS_MEDIAN) {
      warnings.push({ code: 'octaveClefPitchHigh', partId: p.id });
    }
  }
  if (score.tempoMarks.length === 0) warnings.push({ code: 'noTempo' });

  const jumps = summarizeJumps(score);
  if (jumps.length > 0) warnings.push({ code: 'jumpsPresent' });
  const { order, truncated } = unrollMeasures(score.measures);
  if (truncated) warnings.push({ code: 'unrollLimit' });

  const timeline = buildTimeline(score);
  const dividedParts = summarizeDivisions(score, voices);
  const first = score.measures[0];
  const last = score.measures[score.measures.length - 1];
  const firstNominal = first ? (first.timeSignature.beats * 4) / first.timeSignature.beatType : 0;

  const timeSignatures = score.measures
    .filter((m, i) => m.timeSignatureChange || i === 0)
    .map((m) => ({ measureNumber: m.number, beats: m.timeSignature.beats, beatType: m.timeSignature.beatType }));

  const rehearsalMarks = score.measures.flatMap((m) => m.rehearsalMarks.map((label) => ({ label, measureNumber: m.number })));

  const tempoChanges = score.tempoMarks.map((t) => tempoSummary(score, t));

  return {
    summaryVersion: SUMMARY_VERSION,
    title: score.title,
    subtitle: score.subtitle,
    composer: score.composer,
    lyricist: score.lyricist,
    arranger: score.arranger,
    parts,
    measureCount: score.measures.length,
    firstMeasureNumber: first?.number ?? '1',
    lastMeasureNumber: last?.number ?? '1',
    measureNumbers: score.measures.map((m) => m.number),
    hasPickup: !!first && score.measures.length > 1 && (first.implicit || first.length < firstNominal - 1e-9),
    playbackMeasureCount: order.length,
    playbackOrder: order.map((s) => s.measureIndex),
    durationBeats: timeline.totalBeats,
    durationSeconds: Math.round(timeline.totalSec * 1000) / 1000,
    timeSignatures,
    tempo: tempoChanges[0] ?? null,
    tempoChanges,
    repeats: summarizeRepeats(score),
    jumps,
    rehearsalMarks,
    tenorTreble8: parts.some((p) => p.voicePart === 'T' && p.treble8vb),
    dividedParts,
    warnings,
  };
}

/** The part assignments recorded in a summary, ready for buildTimeline. */
export function assignmentsFromSummary(summary: ScoreSummary): Record<string, PartAssignment> {
  const out: Record<string, PartAssignment> = {};
  for (const p of summary.parts) {
    if (p.role === 'accompaniment') out[p.id] = 'accompaniment';
    else if (p.voicePart) out[p.id] = p.voicePart;
  }
  return out;
}
